import { useMemo, type VoidFunctionComponent } from 'react';

import Locator from '../../../../../shared/Locator';
import { prettyMDY, stripURL } from '../../../../../shared/utils';
import { DetailsPanelLink, DetailsPanelList } from '../../../generic/DetailsPanel';
import type { CheckRevisionResponse } from '../../../../../shared/revisions/types';
import type { DetailsPanelListRowProps } from '../../../generic/DetailsPanel/DetailsPanelListRow';

import styles from './Metadata.module.less';

type Props = {
  metadata: CheckRevisionResponse;
};

function metadataToRows(metadata: CheckRevisionResponse) {
  const version = new Locator(metadata.locator).revision.value;

  const rows: DetailsPanelListRowProps[] = [];
  if (version) {
    rows.push({
      label: 'Version',
      content: <span>{version}</span>,
    });
  }

  rows.push({
    label: 'Locator',
    content: <span>{metadata.locator}</span>,
  });

  if (metadata.releaseDate) {
    rows.push({
      label: 'Release Date',
      content: <span>{prettyMDY(metadata.releaseDate)}</span>,
    });
  }

  if (metadata.homepage) {
    rows.push({
      label: 'Homepage',
      content: (
        <DetailsPanelLink to={metadata.homepage} title={metadata.homepage}>
          {stripURL(metadata.homepage)}
        </DetailsPanelLink>
      ),
    });
  }

  return rows;
}

/**
 * Renders the "Metadata" state of the Test Dependency card, which displays
 * details about the provided dependency revision when FOSSA is able to validate
 * it successfully.
 */
const Metadata: VoidFunctionComponent<Props> = ({ metadata }) => {
  const rows = useMemo(() => metadataToRows(metadata), [metadata]);
  return (
    <div>
      <div className={styles.title}>
        <p>{metadata.title}</p>
      </div>
      {metadata.description && (
        <div className={styles.description}>
          <p>{metadata.description}</p>
        </div>
      )}
      <DetailsPanelList rows={rows} />
    </div>
  );
};

export default Metadata;
